import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReminderHighlight } from 'src/components/atoms/reminder-highlight';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { Booking } from 'src/features/bookings/api/types/response/booking.response';
import { useAssignEmployeesToEmployeeGroup } from 'src/features/employees-groups/api/hooks/employees/use-assign-employees-to-employee-group';
import { useDialog } from 'src/hooks/use-dialog';
import { CancelledBookingsSummaryDialog } from './cancelled-bookings-summary-dialog';

interface Props extends DialogActions {
  employeeGroupToken: string;
  employeeToken: string;
  bookingsToManage: Booking[];
}

export const EmployeeHasActiveOrFutureBookingsDialog = ({
  open,
  onCancel,
  employeeGroupToken,
  employeeToken,
  bookingsToManage,
}: Props) => {
  const { t } = useTranslation(['employees', 'common']);

  const [
    cancelAllActiveAndFutureBookings,
    setCancelAllActiveAndFutureBookings,
  ] = useState<boolean>(true);

  const [
    isCancelledBookingsSummaryDialogOpen,
    handleOpenCancelledBookingsSummaryDialog,
    handleCloseCancelledBookingsSummaryDialog,
  ] = useDialog();

  const handleCancel = () => {
    onCancel?.();
    setCancelAllActiveAndFutureBookings(true);
  };

  const {
    mutate: assignEmployeesToEmployeeGroup,
    isPending: isAssigningEmployeesToEmployeeGroup,
  } = useAssignEmployeesToEmployeeGroup(handleCancel);

  const handleChangeDecision = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = JSON.parse(event.target.value);

    if (typeof parsedValue === 'boolean') {
      setCancelAllActiveAndFutureBookings(parsedValue);
    }
  };

  const handleChangeEmployeeGroup = async () => {
    const bookingDecisions = bookingsToManage.map(({ token }) => ({
      bookingToken: token,
    }));

    assignEmployeesToEmployeeGroup({
      employeeGroupToken,
      employeesTokens: [employeeToken],
      bookingsDecisions: {
        moveBookingDecisions: [],
        cancelBookingDecisions: cancelAllActiveAndFutureBookings
          ? bookingDecisions
          : [],
        keepBookingUnchangedDecisions: cancelAllActiveAndFutureBookings
          ? []
          : bookingDecisions,
      },
      bookingsDecisionsReason: t('employees:employees.bookingsDecisionsReason'),
    });

    if (cancelAllActiveAndFutureBookings) {
      handleOpenCancelledBookingsSummaryDialog();
    }
  };

  return (
    <>
      <Dialog
        title={t(
          'employees:employees.employeeHasActiveOrFutureBookingsDialog.title',
        )}
        open={open}
        onCancel={handleCancel}
        cancelButtonText={t('common:button.cancel')}
        onConfirm={handleChangeEmployeeGroup}
        confirmButtonText={t('common:button.confirm')}
        isConfirmButtonSubmitting={isAssigningEmployeesToEmployeeGroup}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ReminderHighlight>
            <Typography variant="body2">
              {t(
                'employees:employees.employeeHasActiveOrFutureBookingsDialog.cautionMessage',
              )}
            </Typography>
          </ReminderHighlight>

          <FormControl>
            <RadioGroup
              aria-labelledby="bookings-decision-radio-buttons-group"
              name="bookings-decision-radio-buttons-group"
              value={cancelAllActiveAndFutureBookings}
              onChange={handleChangeDecision}>
              <FormControlLabel
                value={true}
                control={<Radio />}
                label={t(
                  'employees:employees.employeeHasActiveOrFutureBookingsDialog.cancel.title',
                )}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mb: 2 }}>
                {t(
                  'employees:employees.employeeHasActiveOrFutureBookingsDialog.cancel.description',
                )}
              </Typography>

              <FormControlLabel
                value={false}
                control={<Radio />}
                label={t(
                  'employees:employees.employeeHasActiveOrFutureBookingsDialog.doNotCancel.title',
                )}
              />
              <Typography variant="caption" color="textSecondary">
                {t(
                  'employees:employees.employeeHasActiveOrFutureBookingsDialog.doNotCancel.description',
                )}
              </Typography>
            </RadioGroup>
          </FormControl>
        </Box>
      </Dialog>

      <CancelledBookingsSummaryDialog
        open={isCancelledBookingsSummaryDialogOpen}
        onCancel={handleCloseCancelledBookingsSummaryDialog}
        cancelledBookings={bookingsToManage}
      />
    </>
  );
};
